import { faTrash, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TabPanel } from '@headlessui/react';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import SkeletonLoader from 'tiny-skeleton-loader-react';
import useGetRequest from '../../../api/useGetRequest';
import axios from 'axios';

export default function AddressPhotos({ isFocused, addressKey, clientId }) {
  const BASE_URL = useSelector((state) => state.BASE_URL);
  const [shouldFetch, setShouldFetch] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const { data, isLoading, mutate } = useGetRequest(
    addressKey && shouldFetch && clientId
      ? `/addresses/photos?address_key=${addressKey}&client_id=${clientId}`
      : null,
    0
  );

  useEffect(() => {
    if (isFocused && addressKey && clientId && !shouldFetch) {
      setShouldFetch(true);
    }
  }, [isFocused, addressKey, clientId]);

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette photo ?')) {
      return;
    }

    try {
      const token = localStorage.getItem('fleetToken');
      await axios.delete(`${BASE_URL}/addresses/photos/${photoId}`, {
        headers: {
          Authorization: token,
        },
      });

      mutate();
    } catch (error) {
      console.error('Erreur lors de la suppression de la photo:', error);
      alert('Erreur lors de la suppression de la photo');
    }
  };

  const handleAddPhoto = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Veuillez sélectionner un fichier image');
      e.target.value = '';
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('photo', file);
      formData.append('address_key', addressKey);
      formData.append('client_id', clientId);

      const token = localStorage.getItem('fleetToken');
      await axios.post(`${BASE_URL}/addresses/photos/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: token,
        },
      });

      mutate();
    } catch (error) {
      console.error("Erreur lors de l'upload de la photo:", error);
      alert("Erreur lors de l'upload de la photo");
    } finally {
      setIsUploading(false);
      e.target.value = '';
    }
  };

  if (isLoading) {
    return (
      <TabPanel>
        <div className="tw-mt-6">
          <div className="tw-text-[16px] tw-font-work tw-font-medium tw-leading-4 tw-text-[rgba(0,0,0,0.90)] tw-mb-3">
            Photos du destinataire
          </div>
          <div className="tw-flex tw-gap-3">
            {[...Array(3)].map((_, i) => (
              <SkeletonLoader
                key={i}
                width={120}
                height={120}
                style={{ borderRadius: 8 }}
              />
            ))}
          </div>
        </div>
      </TabPanel>
    );
  }

  const photos = data?.photos || [];

  return (
    <TabPanel>
      <div className="tw-mt-6">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
        <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
          <div className="tw-text-[16px] tw-font-work tw-font-medium tw-leading-4 tw-text-[rgba(0,0,0,0.90)]">
            Photos du destinataire
          </div>
          <button
            onClick={handleAddPhoto}
            disabled={isUploading}
            className="tw-bg-main tw-text-white tw-px-4 tw-py-2 tw-rounded-md tw-text-sm tw-font-medium hover:tw-bg-opacity-90 tw-transition-colors tw-border-none tw-cursor-pointer tw-flex tw-items-center tw-gap-2 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
          >
            <FontAwesomeIcon icon={faPlus} className="tw-w-3 tw-h-3" />
            {isUploading ? 'Upload en cours...' : 'Ajouter une photo'}
          </button>
        </div>

        {photos.length > 0 ? (
          <div className="tw-flex tw-flex-wrap tw-gap-3">
            {photos.map((photo, index) => (
              <div
                key={photo.id}
                className="tw-relative tw-overflow-hidden tw-rounded-lg tw-h-[120px] tw-w-[120px] tw-bg-gray-100 tw-group"
              >
                <a
                  href={photo.photo_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="tw-absolute tw-inset-0 tw-block tw-cursor-pointer"
                >
                  <img
                    src={photo.photo_url}
                    alt={`Photo ${photo.photo_index || index + 1}`}
                    className="tw-h-full tw-w-full tw-object-cover tw-object-center"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      const parent = e.target.parentNode;
                      if (!parent.querySelector('.error-placeholder')) {
                        const placeholder = document.createElement('div');
                        placeholder.className =
                          'error-placeholder tw-w-full tw-h-full tw-bg-gray-100 tw-flex tw-items-center tw-justify-center tw-text-gray-400 tw-text-xs tw-p-2 tw-text-center';
                        placeholder.textContent = 'Image non disponible';
                        parent.appendChild(placeholder);
                      }
                    }}
                  />
                  <div className="tw-absolute tw-inset-0 tw-bg-black tw-bg-opacity-0 group-hover:tw-bg-opacity-20 tw-transition-all tw-flex tw-items-center tw-justify-center">
                    <span className="tw-opacity-0 group-hover:tw-opacity-100 tw-text-white tw-text-xs tw-font-medium tw-transition-opacity">
                      Voir
                    </span>
                  </div>
                </a>
                <button
                  className="tw-absolute tw-bottom-2 tw-right-2 tw-z-10 tw-bg-red-600 tw-border-none tw-rounded-full tw-p-1.5 tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity tw-cursor-pointer hover:tw-bg-red-700"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDeletePhoto(photo.id);
                  }}
                  title="Supprimer la photo"
                >
                  <FontAwesomeIcon
                    icon={faTrash}
                    className="tw-text-white tw-w-3 tw-h-3"
                  />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="tw-text-[13px] tw-font-work tw-font-normal tw-leading-normal tw-tracking-[-0.234px] tw-text-[rgba(0,0,0,0.55)]">
            Aucune photo associée à ce destinataire
          </div>
        )}
      </div>
    </TabPanel>
  );
}
